import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react'
import type { ECharts } from 'echarts'
import { useTheme } from 'styled-components'
import { safeChartInit } from '../../../../utils/echarts'
import type { HistogramChartProps } from '../DistributionSection.types'
import { buildHistogramOption } from './HistogramChart.options'
import {
  ChartWrapper,
  ChartContainer,
  StatsLine,
} from './HistogramChart.styles'

export interface HistogramChartRef {
  exportChart: () => string | null
}

export const HistogramChart = forwardRef<
  HistogramChartRef,
  HistogramChartProps
>((props, ref) => {
  const { data, series, statsLabel, height = 250, emptyLabel } = props
  const theme = useTheme()
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<ECharts | null>(null)
  const hasData = series?.length
    ? series.some((item) => item.data.length > 0)
    : data.length > 0

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (!chartInstance.current) return null
      chartInstance.current.resize()
      return chartInstance.current.getDataURL({
        type: 'png',
        pixelRatio: 3,
        backgroundColor: theme.colors.background.primary,
        excludeComponents: ['toolbox', 'dataZoom'],
      })
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !hasData) return
    const chart = safeChartInit(chartRef.current, undefined, {
      renderer: 'canvas',
      devicePixelRatio: window.devicePixelRatio || 1,
    })
    chartInstance.current = chart
    chart.setOption(buildHistogramOption(props, theme))
    return () => {
      chart.dispose()
      chartInstance.current = null
    }
  }, [props, theme, hasData])

  useEffect(() => {
    // Keep canvases stable while the browser takes the print snapshot.
    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  if (!hasData) {
    return (
      <ChartWrapper>
        <StatsLine>{emptyLabel}</StatsLine>
      </ChartWrapper>
    )
  }

  return (
    <ChartWrapper>
      <ChartContainer
        ref={chartRef}
        $height={height}
        role="img"
        aria-label={props.title}
      />
      {series?.length ? (
        <StatsLine>
          {series.map((item) => (
            <div key={item.name}>
              <strong>{item.name}</strong>: {item.statsLabel}
            </div>
          ))}
        </StatsLine>
      ) : (
        statsLabel && <StatsLine>{statsLabel}</StatsLine>
      )}
    </ChartWrapper>
  )
})

HistogramChart.displayName = 'HistogramChart'
